/**
 * client/src/components/DownloadBook.tsx — "Download this book", and the two honest labels around it.
 *
 * §5.8, and the sentence that decides the whole shape of this file: **the byte count is shown before
 * the tap.** A twelve-hour book is ~280 MB. A person on a train, on a metered plan, tapping a button
 * that says "Download" and discovering afterwards that it spent a quarter of their month's data has
 * been lied to by omission. So nothing starts until a real number is on screen, and the number is
 * **measured from this book's own pages**, not from a constant:
 *
 *   1. `GET /books/:id/pages?state=1` — one request. Which pages have a voice, and how many
 *      characters each one is.
 *   2. Up to eight of those pages, spread evenly through the book, are asked for their narration
 *      plan, which carries the artifact's exact `bytes`.
 *   3. bytes-per-character from that sample × the characters in every narrated page = the estimate.
 *
 * Weighted by characters rather than averaged per page because pages are not the same size, and a
 * sample that lands on three short pages of dialogue would under-quote a book of dense prose by
 * half. The word on screen is **"about"** and it stays "about" until the download finishes, at which
 * point the manifest records what was actually stored and the screen switches to the real figure.
 *
 * ── The three limits this file states rather than hides ──────────────────────────────────────────
 *
 *  · **Space.** `navigator.storage.estimate()` before starting, and a refusal with both numbers in it
 *    if the book will not fit. A download that dies at 71% with a `QuotaExceededError` in a console
 *    nobody is looking at is the worst of the available outcomes.
 *  · **Safari's seven days.** §5.8: Safari evicts script-writable storage after seven days without a
 *    visit *unless the site is on the home screen*. That is why the finished label reads **"available
 *    offline (recently)"** and never "downloaded", and it is why the A2HS invitation appears at this
 *    exact moment — it is the one moment where installing to the home screen means something
 *    concrete, and the only moment a person will believe it.
 *  · **A standalone PWA has its own storage and its own cookie jar.** Downloading in Safari and then
 *    installing gives you a home-screen app with none of it. Said in one line, next to the button.
 *
 * ── Why the audio is written here and not by the service worker ──────────────────────────────────
 *
 * **LAW 14.** `client/src/sw.ts` must not claim `/api/v1/audio/` — a cache-first worker answering a
 * plain `200` to an `<audio>` element's opening `Range: bytes=0-1` is how Safari decides a resource
 * is not seekable, and the book then plays once and refuses to seek, with no error anywhere. So the
 * page fills `ab-audio-v1` itself, and the audio engine's host `fetch` reads it back (see
 * `browserEngineHost` in `client/src/audio/AudioEngine.ts`). Nothing about that path involves Range.
 *
 * The book's *text* is the opposite: it is fetched here with a plain `fetch` precisely so the service
 * worker's `ab-book-v1` handler stores it, which means the download warms the same cache, by the same
 * code path, that will serve those requests in airplane mode. A separate writer here would be a
 * second implementation of one rule, diverging on the case nobody screenshotted.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { formatBytes } from "../App.tsx";
import {
  AUDIO_CACHE,
  getOfflineBook,
  openAudioCache,
  putOfflineBook,
  removeOfflineBook,
  type OfflineBook,
  type OfflinePage,
} from "../lib/idb.ts";

// ─── the wire, read defensively ──────────────────────────────────────────────

interface PageRow {
  id: string;
  ord: number;
  chapterOrd: number;
  charCount: number;
  role: string;
  state?: string;
}

interface NarrationBody {
  audio?: { url?: unknown; bytes?: unknown; artifactSha?: unknown } | null;
}

/** How many pages we ask for a real byte count before quoting a figure. */
const SAMPLE_PAGES = 8;

/**
 * Pages downloaded at once.
 *
 * Three, not one and not twelve. One is a residential uplink idling between round trips on a book
 * with four hundred pages; twelve is a burst of `planPage` calls at cloud-2, each of which is ~9
 * indexed statements and a sha256 per sentence on the same event loop that is serving Range requests
 * to somebody's locked phone (`routes/pages.ts` measured it: 1.21 ms per page). Three keeps the pipe
 * full and leaves the box alone.
 */
const CONCURRENCY = 3;

interface Measured {
  /** Pages with a voice, in reading order. */
  ready: PageRow[];
  /** Total pages in the book, narrated or not — so "38 of 104" is sayable. */
  total: number;
  /** Bytes, estimated from a real sample of this book. `null` when nothing is narrated. */
  estBytes: number | null;
  /** How many pages the sample actually measured, stated so the estimate can be judged. */
  sampled: number;
}

type Phase =
  | { k: "idle" }
  | { k: "measuring" }
  | { k: "ready"; m: Measured; free: number | null }
  | { k: "running"; m: Measured; done: number; bytes: number }
  | { k: "done"; book: OfflineBook }
  | { k: "error"; message: string; fix: string | null };

export interface DownloadBookProps {
  bookId: string;
  editionId: string;
  title: string;
  author: string | null;
  coverSha: string | null;
  /** From `GET /books/:id` — used only to phrase "N of M pages have a voice" before we measure. */
  narratedPages?: number;
  /**
   * Recorded on the stored manifest so `clearPrivateOfflineData()` can tell a book anybody could
   * have opened signed-out from one that was somebody's. See `OfflineBook.visibility`.
   */
  visibility?: "private" | "unlisted" | "public";
}

export function DownloadBook(props: DownloadBookProps) {
  const { bookId, editionId, title, author, coverSha } = props;
  const [phase, setPhase] = useState<Phase>({ k: "idle" });
  const [have, setHave] = useState<OfflineBook | null>(null);
  const cancel = useRef<AbortController | null>(null);

  // What this browser already holds, so the button is "Remove" and not "Download" on the second
  // visit. Re-read after every state change that could have altered it.
  const refresh = useCallback(() => {
    void getOfflineBook(bookId).then(setHave);
  }, [bookId]);
  useEffect(refresh, [refresh]);

  // Abandon an in-flight download if the screen goes away. Without this, navigating off mid-download
  // leaves three fetches and a promise chain writing into a Cache for a component that is gone.
  useEffect(() => () => cancel.current?.abort(), []);

  const supported = typeof caches !== "undefined";

  async function measure() {
    setPhase({ k: "measuring" });
    try {
      const m = await measureBook(bookId, editionId);
      const free = await freeBytes();
      setPhase({ k: "ready", m, free });
    } catch (err) {
      setPhase({ k: "error", message: describe(err), fix: null });
    }
  }

  async function start(m: Measured) {
    const controller = new AbortController();
    cancel.current = controller;
    setPhase({ k: "running", m, done: 0, bytes: 0 });

    /**
     * Ask for persistence at the moment it is deserved.
     *
     * Chrome grants it silently to a site with engagement; Firefox prompts; Safari ignores it and
     * applies the seven-day rule regardless, which is why the label below never says "downloaded".
     * The answer is not checked, because there is nothing useful to do with a "no" except carry on
     * and be honest about eviction — which the finished state does anyway.
     */
    try {
      await navigator.storage?.persist?.();
    } catch {
      /* not available, or refused. Neither changes what happens next. */
    }

    try {
      const book = await downloadBook({
        bookId,
        editionId,
        title,
        author,
        coverSha,
        visibility: props.visibility,
        pages: m.ready,
        pagesTotal: m.total,
        signal: controller.signal,
        onProgress: (done, bytes) => setPhase({ k: "running", m, done, bytes }),
      });
      setPhase({ k: "done", book });
      setHave(book);
    } catch (err) {
      if (controller.signal.aborted) {
        // A cancelled download leaves whatever it already stored in the cache and no manifest row.
        // `evictOrphanAudio()` on the offline screen is what reclaims it — deleting it from inside a
        // catch block would race the fetches that are still unwinding.
        setPhase({ k: "idle" });
        return;
      }
      setPhase({ k: "error", message: describe(err), fix: fixFor(err) });
    } finally {
      cancel.current = null;
    }
  }

  if (!supported) {
    return (
      <p className="ab-hint ab-dl-note">
        This browser won&rsquo;t store a book for offline reading — the Cache API isn&rsquo;t available
        here. Everything else works; you just need a connection.
      </p>
    );
  }

  return (
    <div className="ab-dl">
      <OfflineStyles />

      {have && phase.k !== "running" && phase.k !== "done" && (
        <DownloadedRow
          book={have}
          onRemoved={() => {
            setHave(null);
            setPhase({ k: "idle" });
          }}
        />
      )}

      {phase.k === "done" && (
        <DownloadedRow
          book={phase.book}
          fresh
          onRemoved={() => {
            setHave(null);
            setPhase({ k: "idle" });
          }}
        />
      )}

      {!have && phase.k === "idle" && (
        <button type="button" className="ab-btn ab-btn-ghost ab-btn-wrap" onClick={() => void measure()}>
          Make this book available offline…
        </button>
      )}

      {phase.k === "measuring" && (
        <p className="ab-note">
          <span className="ab-spinner" aria-hidden />
          <span>Working out how big this book is…</span>
        </p>
      )}

      {phase.k === "ready" && <Quote m={phase.m} free={phase.free} onGo={() => void start(phase.m)} onBack={() => setPhase({ k: "idle" })} />}

      {phase.k === "running" && (
        <div className="ab-dl-run">
          <div className="ab-bar">
            <span
              className="ab-bar-fill"
              style={{ width: `${Math.round((phase.done / Math.max(1, phase.m.ready.length)) * 100)}%` }}
            />
          </div>
          <p className="ab-progress-label">
            {phase.done} of {phase.m.ready.length} pages · {formatBytes(phase.bytes)}
          </p>
          <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" onClick={() => cancel.current?.abort()}>
            Stop
          </button>
        </div>
      )}

      {phase.k === "error" && (
        <p className="ab-note ab-note-warn">
          <span>
            {phase.message}
            {phase.fix ? ` ${phase.fix}` : ""}{" "}
            <button type="button" className="ab-link" onClick={() => void measure()}>
              Try again
            </button>
          </span>
        </p>
      )}
    </div>
  );
}

// ─── the quote ───────────────────────────────────────────────────────────────

function Quote({
  m,
  free,
  onGo,
  onBack,
}: {
  m: Measured;
  free: number | null;
  onGo: () => void;
  onBack: () => void;
}) {
  if (m.ready.length === 0) {
    return (
      <p className="ab-note">
        <span>
          None of this book has been narrated yet, so there is nothing to take offline. Narrate a
          chapter first and this will have a number in it.{" "}
          <button type="button" className="ab-link" onClick={onBack}>
            Never mind
          </button>
        </span>
      </p>
    );
  }

  const est = m.estBytes ?? 0;
  // A 10% margin: the estimate is a sample, and refusing at exactly the quota means a download that
  // dies on its last page with a QuotaExceededError, which is the worst possible place to fail.
  const tooBig = free !== null && est > free * 0.9;

  return (
    <div className="ab-dl-quote">
      <p className="ab-dl-size">
        <strong>about {formatBytes(est)}</strong>
        <span>
          {" "}
          · {m.ready.length} of {m.total} pages have a voice
        </span>
      </p>
      <p className="ab-hint">
        Measured from {m.sampled} of this book&rsquo;s own pages, so it is close but not exact. On a
        mobile connection this is {formatBytes(est)} of your data.
        {free !== null && ` You have ${formatBytes(free)} free.`}
      </p>

      {tooBig ? (
        <p className="ab-note ab-note-warn">
          <span>
            This won&rsquo;t fit. The book is about {formatBytes(est)} and this browser will only let the
            site store about {formatBytes(free ?? 0)} more. Remove another downloaded book, or free up
            space on the device.
          </span>
        </p>
      ) : (
        <div className="ab-actions">
          <button type="button" className="ab-btn ab-btn-primary ab-btn-sm" onClick={onGo}>
            Download {formatBytes(est)}
          </button>
          <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" onClick={onBack}>
            Not now
          </button>
        </div>
      )}

      <A2HSInvite reason="download" />
    </div>
  );
}

function DownloadedRow({ book, fresh, onRemoved }: { book: OfflineBook; fresh?: boolean; onRemoved: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="ab-dl-have">
      <p className="ab-dl-size">
        {/* ⚠️ **"available offline (recently)", never "downloaded"** — §5.8. Safari evicts
            script-writable storage after seven days without a visit unless the site is on the home
            screen, and there is no event, no callback and no way to find out except by looking. A
            label that says "downloaded" is a promise this product cannot keep on the device it is
            mainly for. */}
        <strong>Available offline (recently)</strong>
        <span>
          {" "}
          · {formatBytes(book.bytes)} · {book.pages.length} of {book.pagesTotal} pages
        </span>
      </p>
      {fresh && <p className="ab-hint">Turn the wifi off and open it — the text and the voice are both here.</p>}
      <p className="ab-hint ab-hint-quiet">
        Safari clears a website&rsquo;s stored files after seven days without a visit. Adding this to your
        home screen is what stops that.
      </p>
      <button
        type="button"
        className="ab-btn ab-btn-ghost ab-btn-sm"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void removeOfflineBook(book.bookId).finally(() => {
            setBusy(false);
            onRemoved();
          });
        }}
      >
        {busy ? "Removing…" : `Remove the download (${formatBytes(book.bytes)})`}
      </button>
    </div>
  );
}

// ─── measuring ───────────────────────────────────────────────────────────────

async function measureBook(bookId: string, editionId: string): Promise<Measured> {
  const res = await fetch(`/api/v1/books/${encodeURIComponent(bookId)}/pages?state=1`, {
    credentials: "same-origin",
    headers: { accept: "application/json" },
  });
  if (!res.ok) throw new Error(`the page list answered ${res.status}`);
  const body = (await res.json()) as { pages?: PageRow[] };
  const all = Array.isArray(body.pages) ? [...body.pages].sort((a, b) => a.ord - b.ord) : [];
  const ready = all.filter((p) => p.state === "ready");
  if (ready.length === 0) return { ready, total: all.length, estBytes: null, sampled: 0 };

  // Spread the sample through the book. Taking the first eight pages measures the front matter, which
  // is short, atypical and often the only part anybody has narrated.
  const step = Math.max(1, Math.floor(ready.length / SAMPLE_PAGES));
  const sample: PageRow[] = [];
  for (let i = 0; i < ready.length && sample.length < SAMPLE_PAGES; i += step) sample.push(ready[i]!);

  let sampleBytes = 0;
  let sampleChars = 0;
  let sampled = 0;
  for (const page of sample) {
    const audio = await pageArtifact(bookId, editionId, page.id);
    if (!audio) continue;
    sampleBytes += audio.bytes;
    sampleChars += page.charCount;
    sampled++;
  }

  const readyChars = ready.reduce((n, p) => n + (Number.isFinite(p.charCount) ? p.charCount : 0), 0);
  const estBytes =
    sampled === 0
      ? null
      : sampleChars > 0
        ? Math.round((sampleBytes / sampleChars) * readyChars)
        : // Every sampled page reported zero characters, which should not happen and is not worth
          // guessing around: fall back to the flat per-page average rather than dividing by zero.
          Math.round((sampleBytes / sampled) * ready.length);

  return { ready, total: all.length, estBytes, sampled };
}

/** One page's current artifact, or `null` if it has none. Never throws for a page — only for a book. */
async function pageArtifact(
  bookId: string,
  editionId: string,
  pageId: string,
  signal?: AbortSignal,
): Promise<{ url: string; bytes: number } | null> {
  const q = new URLSearchParams({ bookId, editionId });
  const res = await fetch(`/api/v1/pages/${encodeURIComponent(pageId)}/narration?${q}`, {
    credentials: "same-origin",
    headers: { accept: "application/json" },
    signal,
  });
  if (!res.ok) return null;
  const body = (await res.json()) as NarrationBody;
  const url = body.audio?.url;
  const bytes = body.audio?.bytes;
  // LAW 9: absent is not zero. A page whose artifact reports no length is a page we will not claim to
  // have downloaded, because the number on the offline screen is a number somebody reads.
  if (typeof url !== "string" || url === "" || typeof bytes !== "number" || !Number.isFinite(bytes)) return null;
  return { url, bytes };
}

async function freeBytes(): Promise<number | null> {
  try {
    const est = await navigator.storage?.estimate?.();
    if (!est || typeof est.quota !== "number") return null;
    return Math.max(0, est.quota - (est.usage ?? 0));
  } catch {
    return null;
  }
}

// ─── downloading ─────────────────────────────────────────────────────────────

async function downloadBook(args: {
  bookId: string;
  editionId: string;
  title: string;
  author: string | null;
  coverSha: string | null;
  visibility?: "private" | "unlisted" | "public";
  pages: PageRow[];
  pagesTotal: number;
  signal: AbortSignal;
  onProgress: (done: number, bytes: number) => void;
}): Promise<OfflineBook> {
  const { bookId, editionId, pages, signal, onProgress } = args;

  const cache = await openAudioCache();
  if (!cache) throw new Error("This browser refused to open the offline store.");

  // The text first, and deliberately: a download interrupted halfway should leave a book you can
  // still READ offline. Silence is a much smaller loss than a blank page.
  await warmBookText(bookId, args.coverSha !== null, signal);

  const stored: OfflinePage[] = [];
  let bytes = 0;
  let done = 0;
  let failed = 0;

  const queue = [...pages];
  const worker = async (): Promise<void> => {
    for (;;) {
      const page = queue.shift();
      if (!page) return;
      if (signal.aborted) throw new DOMException("aborted", "AbortError");

      const artifact = await pageArtifact(bookId, editionId, page.id, signal);
      if (artifact) {
        try {
          // `cache.add` would re-request; we want the response object so the byte count on screen is
          // what was actually written rather than what the plan predicted.
          const res = await fetch(artifact.url, { credentials: "same-origin", signal });
          if (res.ok) {
            const blob = await res.blob();
            await cache.put(artifact.url, new Response(blob, { headers: { "content-type": res.headers.get("content-type") ?? "audio/mp4" } }));
            stored.push({ pageId: page.id, url: artifact.url, bytes: blob.size });
            bytes += blob.size;
          } else {
            failed++;
          }
        } catch (err) {
          if (signal.aborted) throw err;
          // One page that would not come down is not a failed book. It is a page that will need the
          // network, and the manifest simply will not name it.
          failed++;
        }
      }
      done++;
      onProgress(done, bytes);
    }
  };

  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, Math.max(1, queue.length)) }, worker));

  if (stored.length === 0 && failed > 0) {
    throw new Error("None of this book's audio could be stored.");
  }

  const now = Date.now();
  const book: OfflineBook = {
    bookId,
    editionId,
    title: args.title,
    author: args.author,
    coverSha: args.coverSha,
    pages: stored,
    bytes,
    pagesTotal: args.pagesTotal,
    at: now,
    checkedAt: now,
    // Absent reads as private in `clearPrivateOfflineData`, which is the safe direction: the cost is
    // a re-download, not somebody else's book on a shared phone.
    visibility: args.visibility,
  };
  await putOfflineBook(book);
  return book;
}

/**
 * Pull the book's text, structure and cover through the ordinary `fetch` path.
 *
 * Nothing is written here. The service worker's `ab-book-v1` handler stores every one of these
 * responses on the way past, which means the download warms the cache **through the same code that
 * will read it back offline**. If no service worker is controlling this page — dev, an unsupported
 * browser, a failed registration — these requests simply succeed and are not stored, and the offline
 * screen says the shell is not installed instead of pretending otherwise.
 */
async function warmBookText(bookId: string, hasCover: boolean, signal: AbortSignal): Promise<void> {
  const get = async (path: string): Promise<Response | null> => {
    try {
      const res = await fetch(path, { credentials: "same-origin", headers: { accept: "application/json" }, signal });
      return res.ok ? res : null;
    } catch (err) {
      if (signal.aborted) throw err;
      return null;
    }
  };

  const id = encodeURIComponent(bookId);
  await get(`/api/v1/books/${id}`);
  await get(`/api/v1/books/${id}/start`);
  await get(`/api/v1/books/${id}/pages`);
  if (hasCover) {
    // The same width the shelf and the book screen ask for — a different one is a second entry in the
    // cache and a cover that is still missing offline. `covers.ts` takes an allow-list, and 512 is on
    // it; `BookCover` in App.tsx carries the whole argument for that number.
    try {
      await fetch(`/api/v1/covers/${id}.webp?w=512`, { credentials: "same-origin", signal });
    } catch (err) {
      if (signal.aborted) throw err;
    }
  }

  const res = await get(`/api/v1/books/${id}/chapters`);
  if (!res) return;
  const body = (await res.json()) as { chapters?: { ord: number }[] };
  for (const chapter of body.chapters ?? []) {
    if (typeof chapter?.ord !== "number") continue;
    await get(`/api/v1/books/${id}/chapters/${chapter.ord}`);
  }
}

function describe(err: unknown): string {
  if (err instanceof DOMException && err.name === "QuotaExceededError") {
    return "The device ran out of room for this book.";
  }
  if (err instanceof Error && err.message) return err.message;
  return "The download stopped.";
}

function fixFor(err: unknown): string | null {
  if (err instanceof DOMException && err.name === "QuotaExceededError") {
    return "Remove another downloaded book and try again.";
  }
  return null;
}

// ─── Add to Home Screen ──────────────────────────────────────────────────────

/**
 * The `beforeinstallprompt` event, where it exists.
 *
 * Chrome and Edge fire it and give you a deferred prompt. **WebKit does not**, and never has: on an
 * iPhone the only way to install is Share → Add to Home Screen, by hand. So this hook reports three
 * states rather than two, and the component below shows instructions in the third — a button that
 * does nothing on the product's primary device would be worse than no button.
 */
type InstallState =
  | { k: "installed" }
  | { k: "prompt"; go: () => Promise<void> }
  /** iOS/iPadOS Safari, or any engine that will not offer a programmatic install. */
  | { k: "manual" }
  | { k: "none" };

interface InstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}

export function useInstallPrompt(): InstallState {
  const [deferred, setDeferred] = useState<InstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());

  useEffect(() => {
    const onPrompt = (e: Event) => {
      // Without this the browser shows its own mini-infobar at a moment of its choosing, which is
      // never the moment somebody is looking at a 280 MB number.
      e.preventDefault();
      setDeferred(e as InstallPromptEvent);
    };
    const onInstalled = () => setInstalled(true);
    addEventListener("beforeinstallprompt", onPrompt);
    addEventListener("appinstalled", onInstalled);
    return () => {
      removeEventListener("beforeinstallprompt", onPrompt);
      removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return { k: "installed" };
  if (deferred) {
    return {
      k: "prompt",
      go: async () => {
        await deferred.prompt();
        await deferred.userChoice;
        setDeferred(null);
      },
    };
  }
  return isIosSafari() ? { k: "manual" } : { k: "none" };
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia?.("(display-mode: standalone)").matches) return true;
  // The iOS-only property, which predates the media query and is still the only truth there.
  return (navigator as unknown as { standalone?: boolean }).standalone === true;
}

function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  const ios = /iPad|iPhone|iPod/.test(ua) || (ua.includes("Macintosh") && navigator.maxTouchPoints > 1);
  return ios && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

const A2HS_DISMISSED = "ab.a2hs.dismissed";

/**
 * The invitation, in the two places §5.8 says it means something.
 *
 * `reason="download"` is next to the byte count: installing is what stops Safari deleting the book in
 * seven days, and that is a concrete promise about a thing the person is about to spend data on.
 *
 * `reason="guest"` is **session one**, and the reasoning is the opposite of the usual growth-hack
 * ordering: a guest who installs later gets a home-screen app with **its own storage and its own
 * cookie jar**, so their `gst_` actor does not come with them — and with it goes every reading
 * position and every room seat. Prompting on the third session fires exactly when they have the most
 * to lose. Dismissal is remembered, because an invitation that cannot be refused is a banner.
 */
export function A2HSInvite({ reason, float }: { reason: "download" | "guest"; float?: boolean }) {
  const install = useInstallPrompt();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return localStorage.getItem(A2HS_DISMISSED) === "1";
    } catch {
      return false;
    }
  });

  if (dismissed || install.k === "installed" || install.k === "none") return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(A2HS_DISMISSED, "1");
    } catch {
      /* private mode. It will be offered again, which is the harmless direction. */
    }
  };

  return (
    <div className={float ? "ab-a2hs ab-a2hs-float" : "ab-a2hs"} role={float ? "dialog" : undefined} aria-label="Add to home screen">
      <OfflineStyles />
      <p className="ab-a2hs-title">
        {reason === "download"
          ? "Add audiobook to your home screen"
          : "Keep your place — add audiobook to your home screen"}
      </p>
      <p className="ab-hint">
        {reason === "download"
          ? "Safari deletes a website’s stored files after seven days without a visit. It doesn’t do that to an app on your home screen. There’s also no address bar, and no edge-swipe fighting the page turn."
          : "You’re reading without an account, which is fine — but a home-screen app has its own storage, so installing later starts you over. Installing now keeps this place and any rooms you’re in."}
      </p>
      {install.k === "prompt" ? (
        <div className="ab-actions">
          <button type="button" className="ab-btn ab-btn-primary ab-btn-sm" onClick={() => void install.go()}>
            Add to home screen
          </button>
          <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" onClick={dismiss}>
            No thanks
          </button>
        </div>
      ) : (
        <>
          <p className="ab-hint">
            Tap <strong>Share</strong> at the bottom of Safari, then <strong>Add to Home Screen</strong>.
          </p>
          <button type="button" className="ab-btn ab-btn-ghost ab-btn-sm" onClick={dismiss}>
            Got it
          </button>
        </>
      )}
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────────────────

/**
 * P6's own CSS, hoisted by React 19's `<style precedence>` rather than added to `ui/app.css`.
 *
 * React 19 dedupes on `href` and hoists into `<head>`, so rendering this from four components in one
 * tree emits one stylesheet — which is why every component in this phase renders it rather than
 * relying on somebody else having done so. `precedence="ab-phase"` puts it after the base sheets, so
 * `ui/app.css` and `ui/tokens.css` still own every variable used here.
 *
 * Everything below composes with existing classes (`ab-btn`, `ab-note`, `ab-bar`, `ab-hint`) rather
 * than restyling them: App.tsx's own comment records what happened at books.karti.ai when three
 * copies of one page grid shared their *names* and not their *geometry*.
 */
export function OfflineStyles() {
  return (
    <style href="ab-offline" precedence="ab-phase">{`
      .ab-dl { margin-top: 0.75rem; display: flex; flex-direction: column; gap: 0.5rem; }
      .ab-dl-quote, .ab-dl-have, .ab-a2hs {
        border: 1px solid var(--rule, rgba(128,128,128,.28));
        border-radius: 10px;
        padding: 0.75rem 0.9rem;
        display: flex; flex-direction: column; gap: 0.4rem;
      }
      .ab-dl-size { margin: 0; font-size: 0.95rem; }
      .ab-dl-size span { opacity: 0.72; }
      .ab-dl-run { display: flex; flex-direction: column; gap: 0.4rem; }
      .ab-a2hs-title { margin: 0; font-weight: 600; }
      /* Over the reader, never above it: §5.2 pitfall 1 measures the frame in pixels, so a block
         element in the flow would re-paginate the chapter under whoever is reading it. */
      .ab-a2hs-float {
        position: fixed;
        left: 0.75rem; right: 0.75rem;
        bottom: calc(env(safe-area-inset-bottom, 0px) + 0.75rem);
        margin-inline: auto; max-width: 26rem; z-index: 60;
        background: var(--paper, Canvas); color: var(--ink, CanvasText);
        box-shadow: 0 8px 30px rgba(0,0,0,.22);
      }
      .ab-offline-grid { display: flex; flex-direction: column; gap: 0.6rem; margin: 1rem 0; }
      .ab-offline-row {
        display: grid; grid-template-columns: 2.4rem 1fr auto; gap: 0.75rem; align-items: center;
        border: 1px solid var(--rule, rgba(128,128,128,.28)); border-radius: 10px; padding: 0.6rem 0.75rem;
      }
      .ab-offline-row .ab-cover { width: 2.4rem; height: auto; border-radius: 3px; }
      .ab-offline-row-body { min-width: 0; }
      .ab-offline-row-body strong { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .ab-offline-row-body span { font-size: 0.85rem; opacity: 0.72; }
      .ab-offline-bar { display: flex; flex-wrap: wrap; gap: 0.5rem; align-items: center; margin: 0.5rem 0 1rem; }
      /* 390 px is the target. Anything that needs a second column here loses it instead. */
      @media (max-width: 430px) {
        .ab-offline-row { grid-template-columns: 2rem 1fr; }
        .ab-offline-row > :last-child { grid-column: 1 / -1; }
      }
    `}</style>
  );
}

export { AUDIO_CACHE };
